import { GetString } from 'fluent-react';
import React, { RefObject } from 'react';
import {
  CountryLookback,
  CountryProfile,
  GlobalDatum,
  Location,
  LookBackYearRange,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { gradeToString } from '../../graphQL/Utils';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import { failIfValidOrNonExhaustive } from '../../Utils';
import {
  IndicatorsContainerBase,
} from '../Grid';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Indicator, {
  Status,
  TextWeight,
  Type,
} from '../topPageIndicator/Indicator';
import { adjustRankValueDirection, numberToSign } from '../Utils';
import Histogram, { DiversificationDatum } from './Histogram';

export const callBackYearRange: LookBackYearRange = LookBackYearRange.FifteenYears;

const query = gql`
  query DiversificationIntoNewProductsIndicators(
    $id: ID!, $yearRange: LookBackYearRange!
  ) {
    location: countryProfile(location: $id) {
      diversificationGrade
      diversityRank
    }
    countryLookback(id: $id, yearRange: $yearRange) {
      diversityRankChange
    }
    globalData: globalDatum {
      latestDiversityRankTotal
    }
    allLocations(level: country) {
      id
      isInCountryPages
    }
    allCountryProfiles {
      location {
        id
      }
      diversificationGrade
    }
  }
`;

interface SuccessResponse {
  location: null | {
    diversificationGrade: CountryProfile['diversificationGrade'],
    diversityRank: CountryProfile['diversityRank'],
  };
  countryLookback: null | {
    diversityRankChange: CountryLookback['diversityRankChange'],
  };
  globalData: {
    latestDiversityRankTotal: GlobalDatum['latestDiversityRankTotal'];
  };
  allLocations: Array<{
    id: Location['id'],
    isInCountryPages: Location['isInCountryPages'],
  }>;
  allCountryProfiles: Array<{
    location: {id: Location['id']},
    diversificationGrade: CountryProfile['diversificationGrade'],
  }>;
}

interface Variables {
  id: string;
  yearRange: LookBackYearRange;
}

interface Props extends SlideElementProps {
  countryId: string;
  getFluentString: GetString;
  zIndex: number;
}

const TopOfPageIndicators = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {
    getFluentString, zIndex, countryId,
  } = props;
  const variables: Variables = {
    id: countryId,
    yearRange: callBackYearRange,
  };
  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});

  const economicDiversityGradeLabelNoData = (
    <div>
      <GlossaryTerm
          type={TooltipType.Standard}
          term={
            getFluentString(
              'diversification-into-new-products-top-page-indicators-economic-diversity-grade-label')
          }
          explanation={getFluentString('glossary-economic-diversification-grade-term-definition')}
          citation={undefined}
          align={TooltipAlign.Standard}
          attachment={null}
        />
      </div>
  );
  const diversityRankChangeLabel =
    getFluentString('diversification-into-new-products-top-page-indicators-diversity-rank-change-label');
  const diversityRankLabel =
    getFluentString('diversification-into-new-products-top-page-indicators-diversity-rank-label');

  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = (
      <>
        <Indicator
          type={Type.Text}
          status={Status.Loading}
          label={economicDiversityGradeLabelNoData}
        />
        <Indicator
          type={Type.Text}
          status={Status.Loading}
          label={diversityRankLabel}
        />
        <Indicator
          type={Type.Text}
          status={Status.Loading}
          label={diversityRankChangeLabel}
        />
      </>
    );
  } else if (error !== undefined) {
    console.error(error);
    out = (
      <>
        <Indicator
          type={Type.Text}
          status={Status.Failure}
          label={economicDiversityGradeLabelNoData}
        />
        <Indicator
          type={Type.Text}
          status={Status.Failure}
          label={diversityRankLabel}
        />
        <Indicator
          type={Type.Text}
          status={Status.Failure}
          label={diversityRankChangeLabel}
        />
      </>
    );
  } else if (data !== undefined) {
    const {
      location,
      globalData: { latestDiversityRankTotal },
      allLocations, allCountryProfiles, countryLookback,
    } = data;
    if (location === null || countryLookback === null) {
      out = (
        <>
          <Indicator
            type={Type.Text}
            status={Status.Failure}
            label={economicDiversityGradeLabelNoData}
          />
          <Indicator
            type={Type.Text}
            status={Status.Failure}
            label={diversityRankLabel}
          />
          <Indicator
            type={Type.Text}
            status={Status.Failure}
            label={diversityRankChangeLabel}
          />
        </>
      );
    } else {
      const {
        diversificationGrade, diversityRank,
      } = location;
      const {
        diversityRankChange,
      } = countryLookback;
      const diversificationDatum: DiversificationDatum[] = allLocations.map(d => {
        const countryProfileDatum = allCountryProfiles.find(cp => cp.location.id === d.id);
        return({
          id: d.id,
          diversificationGrade: countryProfileDatum ? countryProfileDatum.diversificationGrade : null,
          isInCountryPages: d.isInCountryPages,
        });
      });
      const histogram = (
        <Histogram
          diversificationDatum={diversificationDatum}
          currentCountryId={countryId}
          getFluentString={getFluentString}
        />
      );
      const economicDiversityGradeLabel = (
        <div>
          <GlossaryTerm
              type={TooltipType.Standard}
              term={
                getFluentString(
                  'diversification-into-new-products-top-page-indicators-economic-diversity-grade-label')
              }
              explanation={getFluentString('glossary-economic-diversification-grade-term-definition')}
              citation={undefined}
              align={TooltipAlign.Standard}
              attachment={histogram}
            />
          </div>
      );
      let callBackYearNum: number;
      if (callBackYearRange === LookBackYearRange.FifteenYears) {
        callBackYearNum = 15;
      } else if (callBackYearRange === LookBackYearRange.TenYears) {
        callBackYearNum = 10;
      } else if (callBackYearRange === LookBackYearRange.FiveYears) {
        callBackYearNum = 5;
      } else if (callBackYearRange === LookBackYearRange.ThreeYears) {
        callBackYearNum = 3;
      } else {
        failIfValidOrNonExhaustive(callBackYearRange, 'Invalid Lookback Range');
        callBackYearNum = 15;
      }
      const absValDiversityRankChange = Math.abs(diversityRankChange);
      const diversityRankUnit: string =
        getFluentString('diversification-into-new-products-top-page-indicators-diversity-rank-change-unit', {
          'callback-years': callBackYearNum,
      });
      const diversityRankSuffix = (diversityRank !== null) ?
                                  getFluentString('ordinal-rank-without-number', {rank: diversityRank}) :
                                  '';
      // Adjust diversity rank change so that the smallest number (position 1)
      // is considered the best instead of the largest number (last place)
      const diversityRankChangeAdjustedDirection = adjustRankValueDirection(diversityRankChange);
      out = (
        <>
          <Indicator
            status={Status.Success}
            type={Type.Text}
            weight={TextWeight.Heavy}
            label={economicDiversityGradeLabel}
            text={diversificationGrade !== null ? gradeToString(diversificationGrade) : 'N/A'}
          />
          <Indicator
            status={Status.Success}
            type={Type.Rank}
            label={diversityRankLabel}
            numerator={diversityRank}
            suffix={diversityRankSuffix}
            denominator={getFluentString(
              'diversification-into-new-products-top-page-indicators-diversity-rank-denominator', {
                'total-countries': latestDiversityRankTotal,
            })}
          />
          <Indicator
            status={Status.Success}
            type={Type.TemporalChange}
            weight={TextWeight.Heavy}
            label={diversityRankChangeLabel}
            quantity={absValDiversityRankChange}
            changeDirection={numberToSign(diversityRankChangeAdjustedDirection)}
            unit={diversityRankUnit}
          />
        </>
      );
    }

  } else {
    out = null;
  }
  const style: React.CSSProperties = {
    zIndex,
  };
  return (
    <IndicatorsContainerBase
      style={style}
      ref={rootElRef}
    >
      {out}
    </IndicatorsContainerBase>
  );
});

export default TopOfPageIndicators;
